<script setup lang="ts">
import { Loader2, ArrowLeft, ChevronRight, Eye, EyeOff } from 'lucide-vue-next'
import { buildAuthCompletedPayload, buildDemoCtaClickedPayload } from '~~/shared/analytics/consent'
import { readPref, writePref } from '~/composables/usePersistedPref'
import { postLoginPath } from '~~/shared/organizations/startPage'
import { isFreeTierProfile } from '~~/shared/organizations/freeTrial'
import { LANDING_VERIFY_EMAIL_PATH } from '~~/shared/landingResearch'
import { usePostHog } from '~/composables/usePostHog'
import { DEMO_BOOKING_URL as DEFAULT_DEMO_BOOKING_URL } from '~~/shared/marketing/demoBooking'

definePageMeta({
  layout: 'default',
})

const {
  user, loading, signIn, signInWithEmail, signUpWithEmail, resetPassword,
  mfaSignInHint, completeTotpSignIn, cancelMfaSignIn, signInWithRecoveryCode, isEmailUnverified,
} = useAuth()
// Every string on this page reads `t` — `auth.mfa.*` for the two-factor step (AlexAmin/marcus#899),
// `auth.login.*` for the rest (MAR-979; it rendered English under the `es` cookie until then).
const { t } = useI18n()
const runtimeConfig = typeof useRuntimeConfig === 'function' ? useRuntimeConfig() : undefined
const demoBookingUrl = computed(() => runtimeConfig?.public?.demoBookingUrl || DEFAULT_DEMO_BOOKING_URL)
const { profile, fetchProfile } = useUserProfile()
const { step } = useOnboarding()
const { captureEvent } = usePostHog()
const route = useRoute()
const router = useRouter()

const tab = ref<'signin' | 'signup' | 'reset'>('signin')
const email = ref('')
const password = ref('')
const confirmPassword = ref('')
const showPassword = ref(false)
const submitting = ref(false)
const error = ref('')
const resetSent = ref(false)
const hydrated = ref(false)
const navigatingAfterAuth = ref(false)
const showAuthGate = computed(() => !hydrated.value || loading.value || !!user.value)
const orgInviteToken = ref('')
const orgInvite = computed(() => decodeOrgInviteToken(orgInviteToken.value))
const hasOrgInvite = computed(() => !!orgInvite.value)

// Two-factor sign-in (AlexAmin/marcus#899). `mfaSignInHint` flips truthy the moment `signIn`/
// `signInWithEmail` catches `auth/multi-factor-auth-required` — the password (or Google popup) has
// already succeeded, and this is the ONLY thing left before `navigateAfterAuth` runs.
const mfaMode = ref<'totp' | 'recovery'>('totp')
const otp = ref('')
const recoveryCode = ref('')

/**
 * `/i/<code>` sends an unusable code here as a bare `?invite_error=1` — no code, no id, no reason
 * (`server/routes/i/[code].get.ts`). Unknown, revoked, expired and used-up are indistinguishable by
 * design, so this line has to be true of all four; saying which one it was is precisely the oracle
 * that route exists to avoid.
 */
const inviteLinkFailed = computed(() => route.query.invite_error !== undefined)

onMounted(async () => {
  hydrated.value = true
  const routeInvite = typeof route.query.invite === 'string' ? route.query.invite : ''
  orgInviteToken.value = routeInvite || readPref('marcus-org-invite-token') || ''
  if (orgInviteToken.value) {
    writePref('marcus-org-invite-token', orgInviteToken.value)
    tab.value = 'signup'
  }
  if (user.value) {
    await navigateAfterAuth()
  }
})

watch([user, loading], async ([currentUser, isLoading]) => {
  if (!hydrated.value || isLoading || !currentUser) return
  await navigateAfterAuth()
})

watch(hasOrgInvite, (value) => {
  if (value) tab.value = 'signup'
})

async function navigateAfterAuth() {
  if (navigatingAfterAuth.value) return
  navigatingAfterAuth.value = true

  const redirect = typeof route.query.redirect === 'string' ? route.query.redirect : ''
  if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
    if (redirect.startsWith('/oauth/authorize')) {
      window.location.assign(redirect)
      return
    }
    await navigateTo(redirect)
    return
  }
  if (orgInviteToken.value) {
    await navigateTo(`/app/setup?invite=${encodeURIComponent(orgInviteToken.value)}`)
    return
  }
  // Refresh the profile for the just-authenticated user, then route on onboarding state.
  await fetchProfile()
  // MAR-1388: logging in without an organization lands on `/chat` (free public chat) rather than
  // forcing organization creation first; visiting `/app` or `/app/setup` shows the buying page. MAR-1452 (D19): a
  // member of their own free-trial organization is a free user too, routed the same way (`free_trial`).
  if (profile && 'value' in profile && profile.value && 'organization_name' in profile.value && isFreeTierProfile(profile.value)) {
    // `/chat` refuses an unverified email and bounces to the verify page, which read as a loop right
    // after sign-up: go there directly (MAR-1465). The `/chat` gate itself is unchanged.
    await navigateTo(isEmailUnverified() ? LANDING_VERIFY_EMAIL_PATH : '/chat')
    return
  }
  // The seat plan's start page (MAR-1172): chat unless the plan says otherwise. Setup comes first.
  await navigateTo(step.value ? '/app/setup' : postLoginPath(profile?.value?.start_page))
}

async function handleGoogle() {
  error.value = ''
  submitting.value = true
  try {
    await signIn()
    captureEvent('auth_completed', buildAuthCompletedPayload({ method: 'google', mode: tab.value === 'signup' ? 'signup' : 'signin', source: '/login' }))
    await navigateAfterAuth()
  } catch (err: any) {
    // Do not show an error banner here: `mfaSignInHint` is now set and the template switches to the
    // two-factor step on its own. Showing "Sign in failed" over it would be actively misleading —
    // nothing failed, the account just has a second factor left to complete.
    if (err?.code !== 'auth/multi-factor-auth-required') {
      error.value = friendlyError(err?.code, 'auth.login.errors.signInFailed')
    }
  } finally {
    submitting.value = false
  }
}

async function handleEmail() {
  error.value = ''
  if (!email.value || !password.value) { error.value = t('auth.login.errors.emailAndPasswordRequired'); return }
  if (tab.value === 'signup' && !confirmPassword.value) {
    error.value = t('auth.login.errors.confirmPasswordRequired')
    return
  }
  if (tab.value === 'signup' && password.value !== confirmPassword.value) {
    error.value = t('auth.login.errors.passwordsDoNotMatch')
    return
  }
  submitting.value = true
  try {
    if (tab.value === 'signin') {
      await signInWithEmail(email.value, password.value)
    } else {
      await signUpWithEmail(email.value, password.value)
    }
    captureEvent('auth_completed', buildAuthCompletedPayload({ method: 'password', mode: tab.value === 'signup' ? 'signup' : 'signin', source: '/login' }))
    await navigateAfterAuth()
  } catch (err: any) {
    if (err?.code !== 'auth/multi-factor-auth-required') {
      error.value = friendlyError(err?.code)
    }
    submitting.value = false
  }
}

async function handleTotpSubmit() {
  error.value = ''
  if (!otp.value) { error.value = t('auth.mfa.codeRequired'); return }
  submitting.value = true
  try {
    await completeTotpSignIn(otp.value)
    captureEvent('auth_completed', buildAuthCompletedPayload({ method: 'password', mode: 'mfa', source: '/login' }))
    await navigateAfterAuth()
  } catch (err: any) {
    error.value = friendlyError(err?.code)
    submitting.value = false
  }
}

async function handleRecoverySignIn() {
  error.value = ''
  if (!recoveryCode.value) { error.value = t('auth.mfa.recoveryCodeRequired'); return }
  submitting.value = true
  try {
    await signInWithRecoveryCode(email.value, recoveryCode.value)
    captureEvent('auth_completed', buildAuthCompletedPayload({ method: 'password', mode: 'mfa', source: '/login' }))
    await navigateAfterAuth()
  } catch {
    // Deliberately generic — see `server/api/auth/mfa/recovery-signin.post.ts` — a specific reason
    // here would tell an attacker which half (email vs. code) was wrong.
    error.value = t('auth.mfa.invalidRecoverySignIn')
    submitting.value = false
  }
}

function backFromMfa() {
  cancelMfaSignIn()
  mfaMode.value = 'totp'
  otp.value = ''
  recoveryCode.value = ''
  error.value = ''
}

async function handleReset() {
  error.value = ''
  if (!email.value) { error.value = t('auth.login.errors.emailRequired'); return }
  submitting.value = true
  try {
    await resetPassword(email.value)
    resetSent.value = true
  } catch (err: any) {
    error.value = friendlyError(err?.code)
  } finally {
    submitting.value = false
  }
}

const FIREBASE_ERROR_KEYS: Record<string, string> = {
  'auth/invalid-credential': 'auth.login.errors.invalidCredential',
  'auth/user-not-found': 'auth.login.errors.userNotFound',
  'auth/wrong-password': 'auth.login.errors.wrongPassword',
  'auth/email-already-in-use': 'auth.login.errors.emailAlreadyInUse',
  'auth/weak-password': 'auth.login.errors.weakPassword',
  'auth/invalid-email': 'auth.login.errors.invalidEmail',
  'auth/too-many-requests': 'auth.login.errors.tooManyRequests',
}

/**
 * The page's own words for a Firebase failure. An unmapped code used to be shown raw — the code, or
 * the SDK's English message — which is exactly what MAR-979 was about; it now gets `fallbackKey`.
 */
function friendlyError(code: string | undefined, fallbackKey = 'auth.login.errors.generic'): string {
  return t((code && FIREBASE_ERROR_KEYS[code]) || fallbackKey)
}

function switchTab(t: typeof tab.value) {
  if (hasOrgInvite.value && t !== 'signup') return
  tab.value = t
  error.value = ''
  resetSent.value = false
}

function clearOrgInvite() {
  writePref('marcus-org-invite-token', null)
  orgInviteToken.value = ''
  tab.value = 'signin'
  error.value = ''
  resetSent.value = false
  confirmPassword.value = ''

  const nextQuery = { ...route.query }
  delete nextQuery.invite
  void router.replace({ path: route.path, query: nextQuery })
}
</script>

<template>
  <div class="login-ink flex items-center justify-center min-h-screen bg-ink text-ink-foreground relative">
    <div v-if="showAuthGate" class="flex flex-col items-center gap-2">
      <Loader2 class="h-6 w-6 animate-spin text-ink-icon" />
      <span class="text-xs text-ink-muted">{{ t('auth.login.checkingSession') }}</span>
    </div>
    <template v-else>
      <NuxtLink to="/" class="absolute top-6 left-6 inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink-foreground transition-colors">
        <ArrowLeft class="h-4 w-4" />
        {{ t('auth.login.backToHome') }}
      </NuxtLink>
      <Card class="w-full max-w-sm">
        <CardHeader class="space-y-1 pb-4">
          <p class="inline-flex items-center">
            <MarcusLogo class="h-8" />
          </p>
          <CardTitle class="text-lg font-semibold tracking-tight">
            {{ mfaSignInHint ? t('auth.mfa.title') : orgInvite ? t('auth.login.welcome') : tab === 'reset' ? t('auth.login.resetPassword') : tab === 'signup' ? t('auth.login.createAccount') : t('auth.signIn') }}
          </CardTitle>
          <p v-if="orgInvite && !mfaSignInHint" class="text-sm text-muted-foreground">
            {{ t('auth.login.invitedToJoin', { org: orgInvite.org_name }) }}
          </p>
          <button
            v-if="orgInvite && !mfaSignInHint"
            type="button"
            class="self-start text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            @click="clearOrgInvite"
          >
            {{ t('auth.login.declineInvite') }}
          </button>
        </CardHeader>

        <CardContent v-if="mfaSignInHint" class="space-y-4">
          <div v-if="error" class="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2">
            <p class="text-xs text-destructive">{{ error }}</p>
          </div>

          <template v-if="mfaMode === 'totp'">
            <p class="text-sm text-muted-foreground">{{ t('auth.mfa.enterCode') }}</p>
            <form class="space-y-3" @submit.prevent="handleTotpSubmit">
              <input
                v-model="otp"
                type="text"
                inputmode="numeric"
                autocomplete="one-time-code"
                maxlength="6"
                placeholder="123456"
                class="w-full h-9 px-3 text-sm tracking-widest text-center rounded-md border border-input bg-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
              <Button type="submit" class="w-full" :disabled="submitting">
                <Loader2 v-if="submitting" class="h-4 w-4 animate-spin mr-2" />
                {{ t('auth.mfa.verify') }}
              </Button>
            </form>
            <button
              type="button"
              class="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              @click="mfaMode = 'recovery'; error = ''"
            >
              {{ t('auth.mfa.useRecoveryCode') }}
            </button>
          </template>

          <template v-else>
            <p class="text-sm text-muted-foreground">{{ t('auth.mfa.recoveryInstructions') }}</p>
            <form class="space-y-3" @submit.prevent="handleRecoverySignIn">
              <input
                v-model="recoveryCode"
                type="text"
                autocomplete="off"
                placeholder="xxxxx-xxxxx"
                class="w-full h-9 px-3 text-sm tracking-widest text-center rounded-md border border-input bg-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
              <Button type="submit" class="w-full" :disabled="submitting">
                <Loader2 v-if="submitting" class="h-4 w-4 animate-spin mr-2" />
                {{ t('auth.mfa.signInWithRecoveryCode') }}
              </Button>
            </form>
            <button
              type="button"
              class="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              @click="mfaMode = 'totp'; error = ''"
            >
              {{ t('auth.mfa.useAuthenticatorApp') }}
            </button>
          </template>

          <button
            type="button"
            class="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline block"
            @click="backFromMfa"
          >
            {{ t('auth.mfa.back') }}
          </button>
        </CardContent>

        <CardContent v-else class="space-y-4">
          <!-- An invite link that did not resolve. One line for all four failure causes. -->
          <div v-if="inviteLinkFailed" class="rounded-md border border-warning-ink/30 bg-warning-ink/10 px-3 py-2">
            <p class="text-xs text-warning-ink">
              {{ t('auth.login.inviteLinkInvalid') }}
            </p>
          </div>

          <!-- Error -->
          <div v-if="error" class="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2">
            <p class="text-xs text-destructive">{{ error }}</p>
          </div>

          <!-- Reset sent confirmation -->
          <div v-if="resetSent" class="rounded-md border border-success/30 bg-success/10 px-3 py-2">
            <p class="text-xs text-success">{{ t('auth.login.resetSent') }}</p>
          </div>

          <!-- Email form -->
          <form class="space-y-3" @submit.prevent="tab === 'reset' ? handleReset() : handleEmail()">
            <div class="space-y-1">
              <label for="login-email" class="text-xs font-medium text-muted-foreground">{{ t('auth.login.email') }}</label>
              <input
                id="login-email"
                v-model="email"
                type="email"
                autocomplete="email"
                :placeholder="t('auth.login.emailPlaceholder')"
                class="w-full h-9 px-3 text-sm rounded-md border border-input bg-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>

            <div v-if="tab !== 'reset'" class="space-y-1">
              <label for="login-password" class="text-xs font-medium text-muted-foreground">{{ t('auth.login.password') }}</label>
              <div class="relative">
                <input
                  id="login-password"
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  :autocomplete="tab === 'signup' ? 'new-password' : 'current-password'"
                  placeholder="••••••••"
                  class="w-full h-9 pl-3 pr-10 text-sm rounded-md border border-input bg-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
                <button
                  type="button"
                  class="absolute inset-y-0 right-0 flex w-9 items-center justify-center rounded-r-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  :aria-label="showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')"
                  :aria-pressed="showPassword"
                  @click="showPassword = !showPassword"
                >
                  <EyeOff v-if="showPassword" class="h-4 w-4" aria-hidden="true" />
                  <Eye v-else class="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>

            <div v-if="tab === 'signup'" class="space-y-1">
              <label for="login-confirm-password" class="text-xs font-medium text-muted-foreground">{{ t('auth.login.confirmPassword') }}</label>
              <div class="relative">
                <input
                  id="login-confirm-password"
                  v-model="confirmPassword"
                  :type="showPassword ? 'text' : 'password'"
                  autocomplete="new-password"
                  placeholder="••••••••"
                  class="w-full h-9 pl-3 pr-10 text-sm rounded-md border border-input bg-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
                <button
                  type="button"
                  class="absolute inset-y-0 right-0 flex w-9 items-center justify-center rounded-r-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  :aria-label="showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')"
                  :aria-pressed="showPassword"
                  @click="showPassword = !showPassword"
                >
                  <EyeOff v-if="showPassword" class="h-4 w-4" aria-hidden="true" />
                  <Eye v-else class="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>

            <Button type="submit" class="w-full" :disabled="submitting">
              <Loader2 v-if="submitting" class="h-4 w-4 animate-spin mr-2" />
              {{ tab === 'reset' ? t('auth.login.sendResetEmail') : tab === 'signup' ? t('auth.login.createAccount') : t('auth.signIn') }}
            </Button>
          </form>

          <!-- Links -->
          <div class="flex items-center justify-between text-xs text-muted-foreground">
            <button
              v-if="tab === 'signin' && !hasOrgInvite"
              type="button"
              class="hover:text-foreground underline-offset-2 hover:underline"
              @click="switchTab('reset')"
            >
              {{ t('auth.login.forgotPassword') }}
            </button>
            <button
              v-if="tab === 'signin' && !hasOrgInvite"
              type="button"
              class="hover:text-foreground underline-offset-2 hover:underline ml-auto"
              @click="switchTab('signup')"
            >
              {{ t('auth.login.createAccount') }}
            </button>
            <button
              v-if="tab !== 'signin' && !hasOrgInvite"
              type="button"
              class="hover:text-foreground underline-offset-2 hover:underline"
              @click="switchTab('signin')"
            >
              {{ t('auth.login.backToSignIn') }}
            </button>
          </div>

          <!-- Divider -->
          <div v-if="tab !== 'reset'" class="relative">
            <div class="absolute inset-0 flex items-center">
              <span class="w-full border-t border-border" />
            </div>
            <div class="relative flex justify-center text-xs">
              <span class="bg-card px-2 text-muted-foreground">{{ t('auth.login.or') }}</span>
            </div>
          </div>

          <!-- Google -->
          <Button
            v-if="tab !== 'reset'"
            type="button"
            variant="outline"
            class="w-full gap-2.5"
            @click="handleGoogle"
          >
            <Loader2 v-if="submitting" class="h-4 w-4 animate-spin" />
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            {{ hasOrgInvite ? t('auth.login.signUpWithGoogle') : t('auth.signInWithGoogle') }}
          </Button>

          <!-- MAR-1352 AC3 + MAR-1366: "Agendar demo" action on the login page -->
          <div class="pt-3 border-t border-border text-center">
            <a
              data-login-demo-cta
              :href="demoBookingUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center justify-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
              @click="captureEvent('demo_cta_clicked', buildDemoCtaClickedPayload({ surface: 'login' }))"
            >
              {{ t('landing.nav.scheduleDemo') }}
              <ChevronRight class="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          </div>
        </CardContent>
      </Card>
    </template>
  </div>
</template>
